import hueRampUrl from '../../assets/baked/hues/hues.webp?url';

/**
 * A CPU-side readout of the baked hue ramp, used to turn a UO hue id into a flat
 * sRGB colour for chat (Slice 5).
 *
 * The body sprites are recoloured on the GPU by the hue *filter* (a per-pixel
 * luminance lookup into this same `32 x count` ramp - see {@link ./hueFilter}).
 * Speech, though, needs one representative colour the chat layer can use on *two*
 * surfaces at once: the Pixi overhead `Text` (a flat-coloured label, not
 * grayscale art, so a filter would be the wrong tool) and the DOM journal's CSS
 * `color`. A GPU filter can colour neither directly, so this module reads the
 * ramp's pixels once and hands back a single hue -> colour function that both
 * surfaces share - so a speaker's overhead text and journal line are always the
 * exact same colour.
 *
 * Reading the PNG is browser-only (it uses a canvas); it is loaded from the same
 * committed `hues.webp` the renderer's hue table loads, so it degrades exactly as
 * the rest of the hue system does - real ramp under `npm run convert`, flat
 * grayscale under the stub assets (where bodies are plain boxes too).
 */
export interface HuePalette {
  /** Ramp width (the 32 gradient steps). */
  width: number;
  /** Ramp height (one row per hue id). */
  height: number;
  /** Row-major RGBA bytes, `width * height * 4` long. */
  data: Uint8ClampedArray;
}

/**
 * Which gradient step represents a hue for chat text: near the bright end so the
 * colour reads on the dark background, but not the lightest step (which washes
 * many hues out to near-white). Expressed as a fraction of the ramp width.
 */
const SAMPLE_FRACTION = 0.78;

/** A safe, legible fallback when a hue is absent (id <= 0) or out of range. */
const FALLBACK_COLOR = 0xffffff;

/**
 * Load the baked hue ramp and read its pixels into memory. Browser-only (it
 * rasterises the PNG through a canvas). Resolves to a {@link HuePalette} that
 * {@link hueColor}/{@link hueCss} sample.
 */
export async function loadHuePalette(): Promise<HuePalette> {
  const response = await fetch(hueRampUrl);
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('huePalette: 2D canvas context unavailable');
  ctx.drawImage(bitmap, 0, 0);
  const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  return { width: bitmap.width, height: bitmap.height, data };
}

/**
 * The representative packed-RGB colour (`0xRRGGBB`) of a 1-based UO hue id. Hue
 * `<= 0` (no hue) or out of range falls back to {@link FALLBACK_COLOR}. Row
 * `hueId - 1` holds that hue's gradient (matching {@link ./hueFilter}'s row math).
 */
export function hueColor(palette: HuePalette, hueId: number): number {
  if (hueId <= 0 || hueId > palette.height) return FALLBACK_COLOR;
  const row = hueId - 1;
  const col = Math.min(palette.width - 1, Math.round((palette.width - 1) * SAMPLE_FRACTION));
  const i = (row * palette.width + col) * 4;
  return (palette.data[i]! << 16) | (palette.data[i + 1]! << 8) | palette.data[i + 2]!;
}

/** The same colour as {@link hueColor}, formatted as a `#rrggbb` CSS string. */
export function hueCss(palette: HuePalette, hueId: number): string {
  return `#${hueColor(palette, hueId).toString(16).padStart(6, '0')}`;
}

/**
 * Render a UO hue as its complete 32-step ramp instead of a single sampled colour.
 * The stops are emitted directly from the baked row used by the GPU recolour path,
 * so wardrobe swatches show the real dark-to-light range of each dye.
 */
export function hueGradientCss(palette: HuePalette, hueId: number): string {
  if (hueId <= 0 || hueId > palette.height) return '#ffffff';
  const row = hueId - 1;
  const stops: string[] = [];
  for (let col = 0; col < palette.width; col += 1) {
    const i = (row * palette.width + col) * 4;
    const color = `rgb(${palette.data[i]!} ${palette.data[i + 1]!} ${palette.data[i + 2]!})`;
    const position = palette.width === 1 ? 0 : (col / (palette.width - 1)) * 100;
    stops.push(`${color} ${position.toFixed(2)}%`);
  }
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
